/**
 * Members feature — zod schemas for API responses and form values.
 * Source of truth for both runtime validation and TS types.
 *
 * Stays in lockstep with src/services/members.js public shapers
 * (publicMember, publicStaffProfile). If those drift, this file must
 * follow — the boundary parse will throw otherwise.
 */

import { z } from 'zod';

/* ─── Response schemas ───────────────────────────────────────────── */

const MemberKind = z.enum(['ai', 'human']);

const PositionRef = z.object({
  id: z.string(),
  name: z.string(),
});

const UnitRef = z.object({
  id: z.string(),
  name: z.string(),
});

export const MemberSchema = z.object({
  id: z.string(),
  kind: MemberKind,
  first_name: z.string(),
  last_name: z.string().nullable(),
  description: z.string().nullable(),
  personality: z.string().nullable(),
  // Only populated on detail responses (list endpoint omits it for size).
  systemPrompt: z.string().nullable(),
  email: z.string().nullable(),
  createdAt: z.string(),
  archivedAt: z.string().nullable(),
  // BP-028: 'manual' (default) | 'starter_signup'. Lets the Welcome page
  // single out the pre-hired starter team. Optional so older fixtures
  // without the field still parse.
  createdVia: z.enum(['manual', 'starter_signup']).optional(),
  positions: z.array(PositionRef),
  units: z.array(UnitRef),
});
export type Member = z.infer<typeof MemberSchema>;

export const MembersListResponseSchema = z.object({
  items: z.array(MemberSchema),
});
export type MembersListResponse = z.infer<typeof MembersListResponseSchema>;

export const MemberDetailResponseSchema = z.object({
  member: MemberSchema,
});
export type MemberDetailResponse = z.infer<typeof MemberDetailResponseSchema>;

export const StaffProfileSchema = z.object({
  id: z.string(),
  first_name: z.string(),
  last_name: z.string().nullable(),
  description: z.string().nullable(),
  personality: z.string().nullable(),
  /** Resolved server-side to tenant overrides where the caller has customized
   * the underlying system positions. Empty array means "no defaults." */
  defaultPositionIds: z.array(z.string()),
  /** Resolved server-side to tenant overrides where the caller has customized
   * the underlying system units. Empty array means "no defaults." */
  defaultUnitIds: z.array(z.string()),
});
export type StaffProfile = z.infer<typeof StaffProfileSchema>;

export const StaffProfilesListResponseSchema = z.object({
  items: z.array(StaffProfileSchema),
});
export type StaffProfilesListResponse = z.infer<typeof StaffProfilesListResponseSchema>;

const ResolvedRef = z.object({
  id: z.string(),
  name: z.string(),
});

export const StaffProfileDetailSchema = z.object({
  id: z.string(),
  first_name: z.string(),
  last_name: z.string().nullable(),
  description: z.string().nullable(),
  personality: z.string().nullable(),
  systemPrompt: z.string(),
  defaultPositions: z.array(ResolvedRef),
  defaultUnits: z.array(ResolvedRef),
});
export type StaffProfileDetail = z.infer<typeof StaffProfileDetailSchema>;

export const StaffProfileDetailResponseSchema = z.object({
  item: StaffProfileDetailSchema,
});
export type StaffProfileDetailResponse = z.infer<typeof StaffProfileDetailResponseSchema>;

export const AddMemberResponseSchema = z.object({
  member: MemberSchema,
});
export type AddMemberResponse = z.infer<typeof AddMemberResponseSchema>;

/* ─── Recommended jobs for a Member ────────────────────────────────── */

export const RecommendedJobSchema = z.object({
  id: z.string(),
  scope: z.enum(['system', 'tenant']),
  title: z.string(),
  description: z.string().nullable(),
  icon: z.string().nullable(),
  score: z.number(),
});
export type RecommendedJob = z.infer<typeof RecommendedJobSchema>;

export const RecommendedJobsResponseSchema = z.object({
  items: z.array(RecommendedJobSchema),
});
export type RecommendedJobsResponse = z.infer<typeof RecommendedJobsResponseSchema>;

/* ─── Add member request ──────────────────────────────────────────── */

export interface AddMemberRequest {
  // Optional: when omitted the agent row is built entirely from the override
  // fields below (custom-from-scratch add). first_name + systemPrompt are
  // then required by the service.
  staffProfileId?: string;
  first_name?: string;
  last_name?: string | null;
  personality?: string;
  systemPrompt?: string;
  positionIds?: string[];
  unitIds?: string[];
}

/* ─── Wizard form schema ─────────────────────────────────────────── */

export const addWizardSchema = z.object({
  memberKind: z.enum(['ai', 'human']),
  description: z.string().trim().max(2000),
  staffProfileId: z.string(),
  // When true, the user chose "Skip" on the Describe-the-position step and
  // will fill first_name / personality / systemPrompt directly in the
  // following steps. The submit handler sends no staffProfileId.
  roleSkipped: z.boolean(),
  first_name: z.string().trim().max(200),
  last_name: z.string().trim().max(200),
  // Single-select. Empty string means "inherit the Staff Profile default."
  positionId: z.string(),
  unitId: z.string(),
  personality: z.string().trim().max(2000),
  systemPrompt: z.string().trim().max(12000),
});

export type AddWizardValues = z.infer<typeof addWizardSchema>;

/* ─── Edit form ──────────────────────────────────────────────────── */

// Schema input/output shapes are identical (no .transform()) so RHF's
// SubmitHandler typing stays simple — empty-string → null and array
// conversion happen in the submit handler.
export const editFormSchema = z.object({
  first_name: z.string().trim().min(1, 'First name is required.').max(200),
  last_name: z
    .string()
    .trim()
    .max(200)
    .nullable()
    .optional()
    .transform((v) => (v === '' || v === undefined ? null : v)),
  description: z.string().max(1000).optional(),
  personality: z.string().max(2000).optional(),
  systemPrompt: z.string().trim().min(1, 'Detailed instructions are required.').max(12000),
  positionIds: z.array(z.string().min(1).max(100)).max(20),
  unitIds: z.array(z.string().min(1).max(100)).max(20),
});

export type EditFormValues = z.infer<typeof editFormSchema>;

// Human Members: display name + email come from the user profile, not
// editable per-tenant. The only fields the tenant owns are assignments.
export const editFormHumanSchema = z.object({
  positionIds: z.array(z.string().min(1).max(100)).max(20),
  unitIds: z.array(z.string().min(1).max(100)).max(20),
});

export type EditFormHumanValues = z.infer<typeof editFormHumanSchema>;

/* ─── Catalog (positions/units pickers) ──────────────────────────── */

export const CatalogItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  /** Whether this row is Linqage-managed ('system') or tenant-authored ('tenant'). */
  scope: z.enum(['system', 'tenant']),
});
export type CatalogItem = z.infer<typeof CatalogItemSchema>;

export const CatalogListResponseSchema = z.object({
  items: z.array(CatalogItemSchema),
});
export type CatalogListResponse = z.infer<typeof CatalogListResponseSchema>;

/* ─── LLM enhance ────────────────────────────────────────────────── */

// Mirrors the keys of ENHANCERS in src/services/llm-enhance.js. Add a new
// kind here when adding one server-side.
export const EnhanceKind = z.enum(['member.instructions', 'member.personality']);
export type EnhanceKind = z.infer<typeof EnhanceKind>;

export const EnhanceResponseSchema = z.object({
  content: z.string(),
});
export type EnhanceResponse = z.infer<typeof EnhanceResponseSchema>;

/**
 * What the PATCH endpoint accepts. Distinct from EditFormValues so callers
 * can omit fields entirely (vs. setting them to null/empty array).
 */
export interface UpdateMemberRequest {
  first_name?: string;
  last_name?: string | null;
  description?: string | null;
  personality?: string | null;
  systemPrompt?: string;
  positionIds?: string[];
  unitIds?: string[];
}

export const UpdateResponseSchema = z.object({ member: MemberSchema });
export type UpdateResponse = z.infer<typeof UpdateResponseSchema>;

export const ArchiveResponseSchema = z.object({ member: MemberSchema });
export type ArchiveResponse = z.infer<typeof ArchiveResponseSchema>;

/* ─── Chat ────────────────────────────────────────────────────────── */

export const ChatSenderSchema = z.enum(['user', 'member', 'system']);
export type ChatSender = z.infer<typeof ChatSenderSchema>;

export const ChatKindSchema = z.enum(['text', 'system_event', 'action_card']);
export type ChatKind = z.infer<typeof ChatKindSchema>;

export const ChatStatusSchema = z.enum(['pending', 'streaming', 'complete', 'failed', 'cancelled']);
export type ChatStatus = z.infer<typeof ChatStatusSchema>;

export const ChatMessageSchema = z.object({
  id: z.string(),
  threadId: z.string(),
  sender: ChatSenderSchema,
  kind: ChatKindSchema,
  content: z.string(),
  payload: z.unknown().nullable(),
  status: ChatStatusSchema,
  failureReason: z.string().nullable(),
  inputTokens: z.number().nullable(),
  outputTokens: z.number().nullable(),
  clientMessageId: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ChatMessage = z.infer<typeof ChatMessageSchema>;

export const ChatSendBodySchema = z.object({
  memberId: z.string(),
  content: z.string(),
  clientMessageId: z.string().optional(),
  attachment_ids: z.array(z.string()).max(10).optional(),
});
export type ChatSendBody = z.infer<typeof ChatSendBodySchema>;

export const ChatSendResponseSchema = z.object({
  userMessage: ChatMessageSchema,
  pendingReply: ChatMessageSchema.nullable(),
});
export type ChatSendResponse = z.infer<typeof ChatSendResponseSchema>;

export const ChatListResponseSchema = z.object({
  items: z.array(ChatMessageSchema),
  hasMore: z.boolean(),
});
export type ChatListResponse = z.infer<typeof ChatListResponseSchema>;

export const ChatCancelResponseSchema = z.object({
  messageId: z.string(),
  status: ChatStatusSchema,
});
export type ChatCancelResponse = z.infer<typeof ChatCancelResponseSchema>;

export const ChatClearResponseSchema = z.object({
  messagesCleared: z.number(),
  deletedAttachmentIds: z.array(z.string()),
  archivedPinIds: z.array(z.string()),
});
export type ChatClearResponse = z.infer<typeof ChatClearResponseSchema>;

export interface ClearChatOptions {
  clearMessages: boolean;
  clearFiles: boolean;
  clearChatPins: boolean;
}

/* ─── Memory ──────────────────────────────────────────────────────── */

export const MemoryEntrySchema = z.object({
  id: z.string(),
  scope: z.object({
    kind: z.enum(['member', 'unit', 'tenant']),
    id: z.string(),
  }),
  source: z.object({
    kind: z.enum(['chat_pin', 'job_artifact']),
    id: z.string(),
    jobId: z.string().nullable(),
  }),
  content: z.string(),
  embeddingModel: z.string(),
  createdAt: z.string(),
  archivedAt: z.string().nullable(),
});
export type MemoryEntry = z.infer<typeof MemoryEntrySchema>;

export const RecordMemoryResponseSchema = z.object({ memory: MemoryEntrySchema });
export type RecordMemoryResponse = z.infer<typeof RecordMemoryResponseSchema>;

export const ListMemberMemoriesResponseSchema = z.object({
  items: z.array(MemoryEntrySchema),
});
export type ListMemberMemoriesResponse = z.infer<typeof ListMemberMemoriesResponseSchema>;

const RetrievedMemoryEntrySchema = z.object({
  resourceKind: z.literal('memory_entry'),
  resourceId: z.string(),
  score: z.number(),
  content: z.string().nullable(),
  sourceKind: z.enum(['chat_pin', 'job_artifact']).nullable(),
  sourceId: z.string().nullable(),
  sourceJobId: z.string().nullable(),
  createdAt: z.string().nullable(),
  archivedAt: z.string().nullable(),
});

const RetrievedChatMessageSchema = z.object({
  resourceKind: z.literal('chat_message'),
  resourceId: z.string(),
  score: z.number(),
  content: z.string().nullable(),
  sender: z.enum(['user', 'member']).nullable(),
  createdAt: z.string().nullable(),
});

export const RetrievedResourceSchema = z.discriminatedUnion('resourceKind', [
  RetrievedMemoryEntrySchema,
  RetrievedChatMessageSchema,
]);
export type RetrievedResource = z.infer<typeof RetrievedResourceSchema>;

export const RetrievedResourcesResponseSchema = z.object({
  items: z.array(RetrievedResourceSchema),
});
export type RetrievedResourcesResponse = z.infer<typeof RetrievedResourcesResponseSchema>;

/* ─── Staff Profile matcher ──────────────────────────────────────── */

export const MatchRequestSchema = z.object({
  description: z.string().trim().min(1).max(2000),
});
export type MatchRequest = z.infer<typeof MatchRequestSchema>;

const MatchSchema = z.object({
  staffProfileId: z.string(),
  confidence: z.number().min(0).max(1),
  justification: z.string(),
});
export const MatchResponseSchema = z.object({
  matches: z.array(MatchSchema),
});
export type MatchResponse = z.infer<typeof MatchResponseSchema>;

/* ─── Add human (dev-only) ───────────────────────────────────────── */

export const HUMAN_ADD_ROLES = ['owner', 'admin', 'editor', 'contributor', 'viewer'] as const;
export type HumanAddRole = (typeof HUMAN_ADD_ROLES)[number];

export const AddHumanBodySchema = z.object({
  first_name: z.string().min(1),
  last_name: z.string().optional(),
  email: z.string().email(),
  password: z.string().min(8),
  role_slug: z.enum(HUMAN_ADD_ROLES),
});
export type AddHumanBody = z.infer<typeof AddHumanBodySchema>;
